import mapboxgl from 'mapbox-gl';
import moment from 'moment-timezone';
import { ORDER_STATUS } from 'src/constants/common';
import { BadgeType } from 'src/interface/common';
import { convertUtcToUserTimezone, getDateWithSuffixFormat, lastLocationUpdateToShowDotColor } from 'src/utils/CommonFunctions';

import { ITrackOrder } from './track.interface';

/**
 * Constants for popup positioning
 */
export const POPUP_DIMENSIONS = {
    HEIGHT: 180,
    WIDTH: 320,
    MARGIN: 20,
} as const;

export const DEFAULT_POPUP_OFFSET = 15;

export const checkDateFormat = (date: string, userTimezone: string, formate: string[] = ['dddd', 'MMM Do']): string => {
    // Convert the input date to user timezone for comparison
    const dateInUserTz = moment.utc(date).tz(userTimezone);

    // Create comparison dates in user timezone
    const todayInUserTz = moment().tz(userTimezone).startOf('day');
    const yesterdayInUserTz = moment().tz(userTimezone).subtract(1, 'days').startOf('day');
    const tomorrowInUserTz = moment().tz(userTimezone).add(1, 'days').startOf('day');
    const startOfWeekInUserTz = moment().tz(userTimezone).startOf('week');
    const endOfWeekInUserTz = moment().tz(userTimezone).endOf('week');

    if (dateInUserTz.isSame(todayInUserTz, 'day')) {
        return 'Today';
    } else if (dateInUserTz.isSame(yesterdayInUserTz, 'day')) {
        return 'Yesterday';
    } else if (dateInUserTz.isSame(tomorrowInUserTz, 'day')) {
        return 'Tomorrow';
    } else if (dateInUserTz.isBetween(startOfWeekInUserTz, endOfWeekInUserTz, null, '[]')) {
        return `${convertUtcToUserTimezone(date, formate[0], true, userTimezone)}`;
    } else {
        return `${getDateWithSuffixFormat(`${convertUtcToUserTimezone(date, formate[1], true, userTimezone)}`)}`;
    }
};

export const checkLocationUpdate = (date: string, userTimezone: string) => {
    if (!date) {
        return '-';
    }
    const givenDate = date;
    const dateUtc = moment.utc(givenDate, 'YYYY-MM-DD HH:mm:ss');
    const nowUtc = moment.utc();
    const startOfWeekUtc = moment.utc().startOf('week');
    const endOfWeekUtc = moment.utc().endOf('week');

    const diffInMinutes = nowUtc.diff(dateUtc, 'minutes');
    const diffInHours = nowUtc.diff(dateUtc, 'hours');

    const yesterdayUtc = moment.utc().subtract(1, 'days').startOf('day');

    if (diffInMinutes < 1) {
        return 'just now';
    } else if (diffInMinutes < 60) {
        return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    } else if (diffInHours < 24) {
        return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    } else if (dateUtc.isSame(yesterdayUtc, 'day')) {
        return `yesterday at ${convertUtcToUserTimezone(date, 'h:mm A', true, userTimezone)}`;
    } else if (dateUtc.isBetween(startOfWeekUtc, endOfWeekUtc, null, '[]')) {
        return `${convertUtcToUserTimezone(date, 'dddd [at] h:mm A', true, userTimezone)}`;
    } else {
        return `${convertUtcToUserTimezone(date, 'MMMM Do YYYY [at] h:mm A', true, userTimezone)}`;
    }
};

// Relative day names render lowercase because the date string always follows a
// status label ("Picking up today", not "Picking up On Today")
const RELATIVE_DAY_WORDS = ['Today', 'Yesterday', 'Tomorrow'];

const toMidSentenceDate = (dateLabel: string): string => (RELATIVE_DAY_WORDS.includes(dateLabel) ? dateLabel.toLowerCase() : dateLabel);

export const getToFormatWiseDateTime = (isDateObj: any, userTimezone: string): string => {
    if (!isDateObj) {
        return '-';
    }
    let isDateTime = '-';

    if (isDateObj?.isActiveTab) {
        if (isDateObj?.hasWindow && isDateObj?.startDateTime && isDateObj?.endDateTime) {
            // Schedule times are locally-scheduled values (not UTC) — format directly with moment
            // rather than converting through UTC to avoid off-by-hour shifts
            const startTimeStr = isDateObj?.startTime ? moment(isDateObj.startTime, 'HH:mm:ss').format('h:mmA') : convertUtcToUserTimezone(isDateObj?.startDateTime, 'h:mmA', true, userTimezone);
            const endTimeStr = isDateObj?.endTime ? moment(isDateObj.endTime, 'HH:mm:ss').format('h:mmA') : convertUtcToUserTimezone(isDateObj?.endDateTime, 'h:mmA', true, userTimezone);
            isDateTime = `${toMidSentenceDate(checkDateFormat(isDateObj?.startDateTime, userTimezone))} between ${startTimeStr} and ${endTimeStr}`;
        } else if (isDateObj?.startDateTime && !isDateObj?.isExpectedDate) {
            const timeStr = isDateObj?.startTime ? moment(isDateObj.startTime, 'HH:mm:ss').format('h:mmA') : convertUtcToUserTimezone(isDateObj?.startDateTime, 'h:mmA', true, userTimezone);
            isDateTime = `${toMidSentenceDate(checkDateFormat(isDateObj?.startDateTime, userTimezone))} at ${timeStr}`;
        } else if (isDateObj?.startDateTime && isDateObj?.isExpectedDate) {
            const dateLabel = checkDateFormat(isDateObj?.startDateTime, userTimezone, ['dddd, MMM Do', 'dddd, MMM Do']);
            isDateTime = RELATIVE_DAY_WORDS.includes(dateLabel) ? dateLabel.toLowerCase() : `on ${dateLabel}`;
        }
    }

    return isDateTime;
};

// Schedule fields from the API are date-only strings (YYYY-MM-DD).
// Anchoring to noon UTC prevents moment.utc().tz() from shifting them
// into the previous calendar day in negative-offset timezones (e.g. UTC-4).
const normalizeScheduleDate = (date: string | null): string | null => {
    if (!date) return null;

    return /^\d{4}-\d{2}-\d{2}$/.test(date) ? `${date}T12:00:00Z` : date;
};

export const getPickupDateObject = (orderScheduleObj: any, isPickup: boolean) => {
    if (!isPickup) return null;

    const startDate = normalizeScheduleDate(orderScheduleObj?.currentPickupStartDate ?? null);
    const endDate = normalizeScheduleDate(orderScheduleObj?.currentPickupEndDate ?? null);
    const startTime = orderScheduleObj?.currentPickupStartTime ?? null;
    const endTime = orderScheduleObj?.currentPickupEndTime ?? null;
    const hasStartTime = !!startTime;
    const hasEndTime = !!endTime;

    return {
        startDateTime: startDate,
        endDateTime: endDate ?? startDate,
        // isPickupWindow is a date-range flag, not a time-window flag.
        // Show "between X and Y" whenever both time fields are present.
        hasWindow: !!(hasStartTime && hasEndTime),
        isActiveTab: isPickup,
        // When no time is provided the date is schedule-only — suppress "at HH:mmA"
        isExpectedDate: !hasStartTime,
        startTime,
        endTime,
    };
};

export const getDeliveryDateObject = (orderScheduleObj: any, isDelivered: boolean) => {
    if (!isDelivered) return null;

    const startDate = normalizeScheduleDate(orderScheduleObj?.currentDeliveryStartDate ?? null);
    const endDate = normalizeScheduleDate(orderScheduleObj?.currentDeliveryEndDate ?? null);
    const startTime = orderScheduleObj?.currentDeliveryStartTime ?? null;
    const endTime = orderScheduleObj?.currentDeliveryEndTime ?? null;
    const hasStartTime = !!startTime;
    const hasEndTime = !!endTime;

    return {
        startDateTime: startDate,
        endDateTime: endDate ?? startDate,
        hasWindow: !!(hasStartTime && hasEndTime),
        isActiveTab: isDelivered,
        isExpectedDate: !hasStartTime,
        startTime,
        endTime,
    };
};

export const getLastLocationUpdateObject = (orderObj: any) => {
    if (orderObj?.lastLocationUpdate?.updatedAt) {
        return {
            startDateTime: orderObj?.lastLocationUpdate?.updatedAt,
        };
    } else if (orderObj?.schedule?.actualPickupDatetime) {
        return {
            startDateTime: orderObj?.schedule?.actualPickupDatetime,
        };
    }

    return null;
};

export const isCheckCommonCarrier = (type?: string | null) => type === 'common' || type === 'broker';

export const getBadgeType = (status: any) => {
    let type: BadgeType = 'success';

    if (status === 'draft' || status === 'cancelled' || status === 'dispatched') {
        type = 'pink';
    } else if (status === 'booked' || status === 'delivered') {
        type = 'lightblue';
    } else if (status === 'in-transit' || status === 'booking_failed') {
        type = 'error';
    } else if (status === 'completed') {
        type = 'gray';
    } else {
        type = 'red';
    }

    return type;
};

export const getDotColor = (order: ITrackOrder): 'primary' | 'red' | 'yellow' | 'green' => {
    const isPickup = order.orderStatus === ORDER_STATUS.BOOKED || order.orderStatus === ORDER_STATUS.DISPATCHED;
    const isDelivered = order.orderStatus === ORDER_STATUS.DELIVERED;

    if (isPickup) return 'primary';
    if (isDelivered) return 'green';

    return lastLocationUpdateToShowDotColor(order?.lastLocationUpdate?.updatedAt ? order.lastLocationUpdate.updatedAt : order?.schedule?.actualPickupDatetime) as
        | 'primary'
        | 'red'
        | 'yellow'
        | 'green';
};

export const getTrackOrderCoordinates = (order: ITrackOrder): [number, number] | null => {
    const originLng = parseFloat(order.originAddress?.longitude);
    const originLat = parseFloat(order.originAddress?.latitude);

    if (isNaN(originLng) || isNaN(originLat)) return null;

    if (order.orderStatus === ORDER_STATUS.DELIVERED) {
        const destLng = parseFloat(order.destinationAddress?.longitude);
        const destLat = parseFloat(order.destinationAddress?.latitude);

        if (!isNaN(destLng) && !isNaN(destLat)) {
            return [destLng, destLat];
        }
    }

    // For in-transit orders, use the truck's last known location if available
    if (order.orderStatus === ORDER_STATUS.IN_TRANSIT && order.lastLocationUpdate?.longitude && order.lastLocationUpdate?.latitude) {
        const locLng = parseFloat(order.lastLocationUpdate.longitude);
        const locLat = parseFloat(order.lastLocationUpdate.latitude);

        if (!isNaN(locLng) && !isNaN(locLat)) {
            return [locLng, locLat];
        }
    }

    return [originLng, originLat];
};

/**
 * Calculate dynamic tooltip positioning based on marker screen position.
 * Prevents the popup from rendering off-screen near map edges.
 */
export const calculateTooltipPosition = (
    coordinates: [number, number],
    mapRef: mapboxgl.Map | null
): {
    popupAnchor: 'top' | 'bottom' | 'left' | 'right' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
    popupOffset: number | [number, number];
} => {
    if (!mapRef) {
        return { popupAnchor: 'bottom', popupOffset: DEFAULT_POPUP_OFFSET };
    }

    const point = mapRef.project(coordinates);
    const mapContainer = mapRef.getContainer();
    const containerRect = mapContainer.getBoundingClientRect();

    const distanceFromTop = point.y;
    const distanceFromBottom = containerRect.height - point.y;
    const distanceFromLeft = point.x;
    const distanceFromRight = containerRect.width - point.x;

    const { HEIGHT: popupHeight, WIDTH: popupWidth, MARGIN: margin } = POPUP_DIMENSIONS;

    let popupAnchor: 'top' | 'bottom' | 'left' | 'right' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' = 'bottom';
    let popupOffset: number | [number, number] = DEFAULT_POPUP_OFFSET;

    const isNearTop = distanceFromTop < popupHeight + margin;
    const isNearBottom = distanceFromBottom < popupHeight + margin;
    const isNearLeft = distanceFromLeft < popupWidth / 2 + margin;
    const isNearRight = distanceFromRight < popupWidth / 2 + margin;

    if (isNearTop && isNearLeft) {
        popupAnchor = 'top-left';
        popupOffset = [10, 10];
    } else if (isNearTop && isNearRight) {
        popupAnchor = 'top-right';
        popupOffset = [-10, 10];
    } else if (isNearBottom && isNearLeft) {
        popupAnchor = 'bottom-left';
        popupOffset = [10, -10];
    } else if (isNearBottom && isNearRight) {
        popupAnchor = 'bottom-right';
        popupOffset = [-10, -10];
    } else if (isNearTop) {
        popupAnchor = 'top';
        popupOffset = DEFAULT_POPUP_OFFSET;
    } else if (isNearBottom) {
        popupAnchor = 'bottom';
        popupOffset = DEFAULT_POPUP_OFFSET;
    } else if (isNearRight) {
        popupAnchor = 'right';
        popupOffset = DEFAULT_POPUP_OFFSET;
    } else if (isNearLeft) {
        popupAnchor = 'left';
        popupOffset = DEFAULT_POPUP_OFFSET;
    }

    return { popupAnchor, popupOffset };
};

export const getOrderTypeBadge = (type: string): any => {
    if (type === 'ltl') return 'success';
    if (type === 'ftl') return 'lightblue';

    return 'pink';
};

// The portal's marker palette is three colors, because the API only ever produces
// three states. Pickup and Delivery tabs are two-bucket (completed | pending) → blue,
// or orange once a pending pickup passes its rollover threshold. In Transit is
// two-bucket (on_track | delayed) → blue or red.
//
// There is deliberately NO yellow. WAL has one for its 'require_update' bucket, but
// listRadarOrders.controller.ts folds require_update into 'on_track' for CP and never
// emits it, so a yellow branch here would be unreachable — and a fourth color implies
// a fourth state the portal doesn't have.
export type TrackOrderHealth = 'blue' | 'orange' | 'red';

export const TRACK_HEALTH_COLORS: Record<TrackOrderHealth, string> = {
    red: '#fe594d',
    orange: '#F38744',
    blue: '#6172f3',
};

export const TRACK_HEALTH_HOVER_COLORS: Record<TrackOrderHealth, string> = {
    red: '#D92D20',
    orange: '#EF6820',
    blue: '#444ce7',
};

// Severity rank used by the cluster layer's `max` accumulation so a cluster takes
// the color of the worst load it contains.
export const TRACK_HEALTH_WEIGHTS: Record<TrackOrderHealth, number> = {
    red: 2,
    orange: 1,
    blue: 0,
};

// ─── Rollover (orange) ────────────────────────────────────────────────────────
// Verbatim port of the rollover math in wal-frontend-v2 src/app/Radar/radar.helpers.ts
// (getLeadHours / resolveWindowAnchorTime / getLegRolloverState). Keep the two in sync —
// if the tiers or anchor rules change there, they must change here, or the same load
// renders a different color in WAL and the customer portal.
//
// Ported for MARKER COLOR ONLY. The portal deliberately has no rollover sub-status
// bucket and no rollover action icon; the backend still folds rollover into 'pending'
// (see listRadarOrders.controller.ts). WAL behaves the same way — its Overview strip
// reads "Rollover 0" while these markers are orange.

// Lead time tiers, in hours. First tier whose `maxTransit` exceeds transitHours wins.
const LEAD_HOURS_TIERS: ReadonlyArray<{ maxTransit: number; leadHours: number }> = [
    { maxTransit: 12, leadHours: 2 }, // <12h transit → 2h lead   (same-day shipment)
    { maxTransit: 48, leadHours: 6 }, // 12h–48h      → 6h lead   (1–2 day shipment)
    { maxTransit: Infinity, leadHours: 24 }, // >48h  → 24h lead  (multi-day shipment)
];

// Fallback for windowType='open' when the shipper/consignee has no business hours saved.
const BUSINESS_HOURS_FALLBACK = { start: '09:00:00' };

const getLeadHours = (transitHours: number): number => {
    for (const tier of LEAD_HOURS_TIERS) {
        if (transitHours < tier.maxTransit) return tier.leadHours;
    }

    return LEAD_HOURS_TIERS[LEAD_HOURS_TIERS.length - 1].leadHours;
};

// Window type anchor rules:
//   ready_time / delivery_time → endTime (rollover before the window closes)
//   appointment / by_appointment → endTime (the appointment instant)
//   open → business-hours start (shipper for pickup, consignee for delivery)
//   anything else → startTime (legacy fallback)
const resolveWindowAnchorTime = (windowType: string | null | undefined, startTime: string | null, endTime: string | null, businessHoursStart: string | null): string | null => {
    switch (windowType) {
        case 'ready_time':
        case 'delivery_time':
        case 'appointment':
        case 'by_appointment':
            return endTime ?? startTime;
        case 'open':
            return businessHoursStart || BUSINESS_HOURS_FALLBACK.start;
        default:
            return startTime ?? endTime;
    }
};

interface IRolloverSide {
    startDate: string | null;
    startTime: string | null;
    endTime: string | null;
    windowType: string | null;
    timezone: string | null;
}

// Build the absolute instant for one side (pickup or delivery). Returns null when
// there isn't enough information to anchor (no date, or an invalid moment).
const buildAnchorMoment = (fields: IRolloverSide, businessHoursStart: string | null): moment.Moment | null => {
    if (!fields.startDate) return null;

    const time = resolveWindowAnchorTime(fields.windowType, fields.startTime, fields.endTime, businessHoursStart);
    const datetime = time ? `${fields.startDate} ${time}` : null;

    if (!datetime) return null;

    const anchor = moment.tz(datetime, 'YYYY-MM-DD HH:mm:ss', fields.timezone || 'UTC');

    return anchor.isValid() ? anchor : null;
};

export interface ITrackRolloverState {
    isPickupRollover: boolean;
}

/**
 * Whether a leg has crossed its PICKUP rollover threshold.
 *
 * The delivery side is still read — it sets the transit duration that selects the
 * lead-time tier — but no delivery rollover is returned, because nothing consumes it
 * (see isTrackRolloverDue for why the IN_TRANSIT branch is not ported).
 *
 * `userTimezone` anchors "now" for consistency with the rest of Radar, but the
 * comparison itself uses absolute instants so cross-timezone legs evaluate
 * correctly regardless of the viewer's offset.
 */
export const getTrackRolloverState = (order: ITrackOrder, userTimezone: string): ITrackRolloverState => {
    const empty: ITrackRolloverState = { isPickupRollover: false };
    const sch = order?.schedule;

    if (!sch?.currentPickupStartDate || !sch?.currentDeliveryStartDate) return empty;

    const pickupAt = buildAnchorMoment(
        {
            startDate: sch.currentPickupStartDate,
            startTime: sch.currentPickupStartTime,
            endTime: sch.currentPickupEndTime,
            windowType: sch.currentPickupWindowType,
            timezone: sch.pickupTimezone,
        },
        order?.shipperBusinessHoursStartTime ?? BUSINESS_HOURS_FALLBACK.start
    );

    const deliveryAt = buildAnchorMoment(
        {
            startDate: sch.currentDeliveryStartDate,
            startTime: sch.currentDeliveryStartTime,
            endTime: sch.currentDeliveryEndTime,
            windowType: sch.currentDeliveryWindowType,
            timezone: sch.deliveryTimezone,
        },
        order?.consigneeBusinessHoursStartTime ?? BUSINESS_HOURS_FALLBACK.start
    );

    if (!pickupAt || !deliveryAt) return empty;

    const transitHours = deliveryAt.diff(pickupAt, 'hours', true);

    // transit <= 0 means the delivery anchor is at or before pickup — invalid for transit
    // math, but pickup rollover is still meaningful on its own. Fall back to the
    // smallest tier (2h) so it can still fire.
    const leadHours = transitHours > 0 ? getLeadHours(transitHours) : getLeadHours(0);
    const now = moment().tz(userTimezone || 'UTC');

    return {
        isPickupRollover: !sch.actualPickupDatetime && !now.isBefore(pickupAt.clone().subtract(leadHours, 'hours')),
    };
};

/**
 * Gate the raw rollover state on leg status. PICKUP-SIDE ONLY: a rollover turns the
 * marker orange only while the leg is DISPATCHED and not yet picked up — the Pending
 * case this was built for.
 *
 * WAL's isLegRolloverDue additionally fires on delivery rollover while IN_TRANSIT.
 * That branch is deliberately NOT ported: in the portal it repaints in-transit orders
 * the backend reports as 'on_track' (Overview: "On Track 20 / Delayed 0"), which is the
 * exact class of client-side delay inference getTrackOrderHealth exists to prevent.
 * In-transit color stays owned by radarSubStatus alone. Do not add the IN_TRANSIT
 * branch here without changing the in-transit contract first.
 */
export const isTrackRolloverDue = (order: ITrackOrder, rollover: ITrackRolloverState): boolean => order?.orderStatus === ORDER_STATUS.DISPATCHED && rollover.isPickupRollover;

/**
 * Marker / cluster health for a radar order.
 *
 * `radarSubStatus` from the backend is the ONLY source of truth — never re-derive
 * delay client-side. The customer portal exposes exactly two buckets per tab
 * (listRadarOrders.controller.ts): in-transit rows are either 'delayed' or
 * 'on_track', and the WAL-only 'rollover' / 'require_update' buckets are folded
 * into 'pending' / 'on_track' by that same SQL. The Overview counts come from the
 * identical predicate, so anything computed here instead of read from the API is
 * guaranteed to contradict the strip the user is looking at.
 *
 * Regression this guards: the map used to fall back to "delivery date passed → red"
 * and "no location ping in 8h → yellow" for every in-transit order, painting the
 * whole map red while the Overview correctly reported "On Track 20 / Delayed 0".
 *
 * `isRolloverDue` is the one signal NOT carried by radarSubStatus — it is computed
 * client-side (getTrackRolloverState + isTrackRolloverDue) and passed in by the
 * caller, keeping this a pure function safe to call inside the marker loop. It ranks
 * below 'delayed', which beats it.
 */
export const getTrackOrderHealth = (order: ITrackOrder, isRolloverDue = false): TrackOrderHealth => {
    if (order?.radarSubStatus === 'delayed') return 'red';

    if (isRolloverDue) return 'orange';

    return 'blue';
};

/**
 * Normalizes what the Radar search box holds into the value sent to the API
 * (Table View + Map View sidebar).
 *
 * Typing spaces stays allowed — the box keeps exactly what was typed — but a
 * whitespace-only entry normalizes to '' so the caller can skip the request, and inner
 * runs of spaces collapse so "TEST   CITY" and "TEST CITY" resolve to the same query.
 */
export const normalizeTrackSearchQuery = (value: string): string => (value ?? '').trim().replace(/\s+/g, ' ');
